import React, { useRef, useEffect, useState } from 'react';
import Waypoint from './Waypoint';
import WaypointLine from './WaypointLine';

export const QuestMap = ({ items, achievedWaypointIndex, waypointClicked }) => {
  const svgRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  const itemsPerRow = 5; // Anzahl der Wegpunkte pro Zeile
  const rowHeight = 200; // Höhe jeder Zeile
  const verticalOffset = 80; // Vertikaler Offset von oben
  const horizontalOffset = 60; // Horizontaler Offset von links und rechts

  // Aktualisiert die Breite und Höhe basierend auf dem Container
  useEffect(() => {
    if (svgRef.current) {
      const totalRows = Math.ceil(items?.length / itemsPerRow);
      const calculatedHeight = totalRows * rowHeight + verticalOffset;

      setSize({
        width: svgRef.current.clientWidth,
        height: calculatedHeight
      });
    }
  }, [items]); // Effekt hängt von den Items ab

  // Berechnet Koordinaten für jeden Punkt in einer Schlangenlinie
  const calculateCoordinates = (index) => {
    const rowNumber = Math.floor(index / itemsPerRow);
    const isEvenRow = rowNumber % 2 === 0;
    const positionInRow = index % itemsPerRow;

    // Anpassung der Wegpunktbreite, Zentrierung und Berücksichtigung des horizontalen Offsets
    const effectiveWidth = size.width - horizontalOffset * 2;
    const itemSpacing = effectiveWidth / (itemsPerRow - 1);
    const xOffset = horizontalOffset + (isEvenRow
      ? positionInRow * itemSpacing
      : (itemsPerRow - 1 - positionInRow) * itemSpacing);
    const y = verticalOffset + rowNumber * rowHeight;

    return { x: xOffset, y };
  };

  const calculateDirectionToNextPoint = (currentIndex) => {
    const isLastInRow = currentIndex % itemsPerRow === itemsPerRow - 1;
    const isFirstInRow = currentIndex % itemsPerRow === 0;
    const isEvenRow = Math.floor(currentIndex / itemsPerRow) % 2 === 0;
    const isFirstOverall = currentIndex === 0; // Erster Wegpunkt insgesamt
  
    if (isFirstOverall) {
      return "standard"; // Sonderfall für den ersten Wegpunkt
    } else if (isEvenRow) {
      if (isLastInRow) return "upper-corner-right";
      if (isFirstInRow) return "lower-corner-right";
    } else {
      if (isLastInRow) return "upper-corner-left";
      if (isFirstInRow) return "lower-corner-left";
    }
  
    return "standard";
  };

  // Zeichnet Linien zwischen den Punkten
  const renderLines = () => {
    return items?.slice(1).map((item, index) => {
      const start = calculateCoordinates(index);
      const end = calculateCoordinates(index + 1);

      return (
        <WaypointLine
          key={index}
          start={start}
          end={end}
          isCompleted={index < achievedWaypointIndex}
        />
      );
    });
  };
  
  const handleWaypointClick = (item) => {      
    waypointClicked(item);
  };

  const renderWaypoints = () => {
    return items?.map((item, index) => {
      const coordinates = calculateCoordinates(index);
      const directionToNext = calculateDirectionToNextPoint(index);
      return (
        <Waypoint
          key={index}
          item={item}
          coordinates={coordinates}
          directionToNext={directionToNext}
          onClick={handleWaypointClick}
          isCompleted={index < achievedWaypointIndex}
          isActive={index === achievedWaypointIndex}
          isNext={index === achievedWaypointIndex + 1 && index < items.length}
        />
      );
    });
  };

  return (
    <svg ref={svgRef} width="100%" height="100%" viewBox={`0 0 ${size.width} ${size.height}`}>
        <defs>
          <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
            <feOffset result="offOut" in="SourceGraphic" dx="3" dy="3" />
            <feGaussianBlur result="blurOut" in="offOut" stdDeviation="3" />
            <feBlend in="SourceGraphic" in2="blurOut" mode="normal" />
          </filter>
        </defs>
      {renderLines()}
      {renderWaypoints()}
    </svg>
  );
};
